import {
  PlainLink,
  Scrollable,
  SkeletonShape,
  FormattedRelativeTime,
  Truncated,
  Flex,
  Icon,
  Text,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { ServicesApiRecentResource } from '@/services/api/services-api';
import { useAnalytics } from '@/services/analytics';
import { useResourceInfo } from '../hooks';
import { generatePath } from '@/services/router/generate-path';

import * as styles from './recents-row.css';

export function resourceHref(workspaceId: number, resource: ServicesApiRecentResource): string {
  const wid = String(workspaceId);
  switch (resource.type) {
    case 'campaign':
      return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
        workspaceId: wid,
        campaignId: resource.id,
      });
    case 'newsletter':
      return generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId', {
        workspaceId: wid,
        newsletterId: resource.id,
      });
    case 'transactional':
      return generatePath('/workspaces/:workspaceId/journeys/transactional/:transactionalMessageId', {
        workspaceId: wid,
        transactionalMessageId: resource.id,
      });
    case 'segment':
      return generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
        workspaceId: wid,
        segmentId: resource.id,
      });
    case 'in_app_broadcast':
      return generatePath('/workspaces/:workspaceId/journeys/anonymous/:inAppBroadcastId', {
        workspaceId: wid,
        inAppBroadcastId: resource.id,
      });
    case 'ds_node':
      return generatePath('/workspaces/:workspaceId/design-studio/editor/:id', {
        workspaceId: wid,
        id: resource.id,
      });
    case 'snippet':
      return generatePath('/workspaces/:workspaceId/journeys/snippets/:snippetId', {
        workspaceId: wid,
        snippetId: resource.id,
      });
    default: {
      const _exhaustive: never = resource.type;
      return String(_exhaustive);
    }
  }
}

interface RecentCardProps {
  resource: ServicesApiRecentResource;
  workspaceId: number;
}

const DS_NODE_TYPE_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  COMPONENT: 'Component',
  WHATSAPP: 'WhatsApp',
  FOLDER: 'Folder',
};

const DS_NODE_TYPE_ICONS: Record<string, IconName> = {
  EMAIL: 'email',
  COMPONENT: 'blocks',
  WHATSAPP: 'chat',
  FOLDER: 'folder',
};

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  triggered_broadcast: 'API-Triggered Broadcast',
  behavioral: 'Campaign',
  seg_attr: 'Campaign',
  date: 'Campaign',
  form: 'Campaign',
  webhook: 'Campaign',
  transactional: 'Campaign',
  relationship: 'Campaign',
  object: 'Campaign',
};

function RecentCard({ resource, workspaceId }: RecentCardProps) {
  const { track } = useAnalytics();
  const {
    name,
    campaignType,
    dsNodeType,
    isLoading: isResourceLoading,
    isNotFound,
  } = useResourceInfo(workspaceId, resource.type, resource.id);

  if (isNotFound) {
    return null;
  }

  let typeLabel: string;
  let typeIcon: IconName;
  switch (resource.type) {
    case 'campaign':
      typeLabel = campaignType != null ? CAMPAIGN_TYPE_LABELS[campaignType] ?? 'Campaign' : 'Campaign';
      typeIcon = 'campaigns';
      break;
    case 'newsletter':
      typeLabel = 'Broadcast';
      typeIcon = 'broadcast';
      break;
    case 'transactional':
      typeLabel = 'Transactional';
      typeIcon = 'transactional-api';
      break;
    case 'segment':
      typeLabel = 'Segment';
      typeIcon = 'segment';
      break;
    case 'in_app_broadcast':
      typeLabel = 'Anonymous';
      typeIcon = 'anonymous';
      break;
    case 'ds_node':
      typeLabel = dsNodeType != null ? DS_NODE_TYPE_LABELS[dsNodeType] ?? 'Design Studio' : 'Design Studio';
      typeIcon = dsNodeType != null ? DS_NODE_TYPE_ICONS[dsNodeType] ?? 'design-studio' : 'design-studio';
      break;
    case 'snippet':
      typeLabel = 'Snippet';
      typeIcon = 'editor-code';
      break;
    default: {
      const _exhaustive: never = resource.type;
      typeLabel = String(_exhaustive);
      typeIcon = 'campaigns';
    }
  }

  // DS nodes need the type resolved before we can build the correct href
  if (resource.type === 'ds_node' && dsNodeType == null && isResourceLoading) {
    return <RecentCardSkeleton />;
  }

  let href = resourceHref(workspaceId, resource);
  if (resource.type === 'ds_node' && dsNodeType === 'FOLDER') {
    href = generatePath('/workspaces/:workspaceId/design-studio/dashboard', {
      workspaceId: String(workspaceId),
    });
  }

  return (
    <PlainLink
      href={href}
      className={styles.cardLink}
      onClick={() => track('home', 'recentCardClicked', { 'Resource Type': resource.type })}
    >
      <div className={styles.card}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="050">
            <Icon name={typeIcon} size="xs" color="subtle" />
            <Text size="xs" color="subtle">
              {typeLabel}
            </Text>
          </Flex>
          <Text size="m" color="subtle">
            ›
          </Text>
        </Flex>
        <Truncated size="s" weight="semibold">
          {name ?? <SkeletonShape style={{ width: '80%', height: 16 }} />}
        </Truncated>
        <Text size="xs" color="subtle">
          <FormattedRelativeTime date={resource.updated_at * 1000} />
        </Text>
      </div>
    </PlainLink>
  );
}

function RecentCardSkeleton() {
  return (
    <div className={styles.cardLink}>
      <div className={styles.card}>
        <Flex alignItems="center" justifyContent="space-between">
          <SkeletonShape style={{ width: 80, height: 14 }} />
          <Text size="m" color="subtle">
            ›
          </Text>
        </Flex>
        <SkeletonShape style={{ width: '80%', height: 16 }} />
        <SkeletonShape style={{ width: 60, height: 14 }} />
      </div>
    </div>
  );
}

const MIN_CARDS = 4;

interface CreateCardItem {
  label: string;
  icon: IconName;
  getHref: (workspaceId: string) => string;
}

const CREATE_CARDS: CreateCardItem[] = [
  {
    label: 'Create a broadcast',
    icon: 'broadcast',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/broadcasts/new', { workspaceId: wid }),
  },
  {
    label: 'Create a segment',
    icon: 'segment',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/segments/explore', { workspaceId: wid }),
  },
  {
    label: 'Create a transactional message',
    icon: 'transactional-api',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/transactional/new', { workspaceId: wid }),
  },
  {
    label: 'Create a goal',
    icon: 'goal',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/goals/new', { workspaceId: wid }),
  },
];

function CreateCard({ item, workspaceId }: { item: CreateCardItem; workspaceId: number }) {
  const { track } = useAnalytics();
  const href = item.getHref(String(workspaceId));

  return (
    <PlainLink
      href={href}
      className={styles.cardLink}
      onClick={() => track('home', 'recentCardClicked', { 'Resource Type': 'create' })}
    >
      <div className={styles.createCard}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="050">
            <Icon name={item.icon} size="xs" color="subtle" />
          </Flex>
          <Icon name="add" size="xs" color="subtle" />
        </Flex>
        <Text size="s" weight="semibold" color="subtle">
          {item.label}
        </Text>
      </div>
    </PlainLink>
  );
}

interface RecentsRowProps {
  recents: ServicesApiRecentResource[] | undefined;
  isLoading: boolean;
  workspaceId: number;
}

export function RecentsRow({ recents, isLoading, workspaceId }: RecentsRowProps) {
  if (isLoading) {
    return (
      <Scrollable overflow="x" display="flex" gap="200" className={styles.container}>
        {Array.from({ length: 5 }, (_, index) => (
          <RecentCardSkeleton key={index} />
        ))}
      </Scrollable>
    );
  }

  const recentCount = recents?.length ?? 0;
  const fillCount = Math.max(0, MIN_CARDS - recentCount);
  const createCards = CREATE_CARDS.slice(0, fillCount);

  if (recentCount === 0 && createCards.length === 0) {
    return null;
  }

  return (
    <Scrollable overflow="x" display="flex" gap="200" className={styles.container} withEdgeIndicators>
      {recents?.map((resource) => (
        <RecentCard key={`${resource.type}-${resource.id}`} resource={resource} workspaceId={workspaceId} />
      ))}
      {createCards.map((item) => (
        <CreateCard key={item.label} item={item} workspaceId={workspaceId} />
      ))}
    </Scrollable>
  );
}
